// @flow

import React, {useState} from 'react';
import CustomAvatar from '../../../../../../../../elements/CustomAvatar/CustomAvatar';
import {makeStyles} from '@material-ui/core';

import type {Theme} from '@material-ui/core';
import {colors} from '../../../../../../../../assets/styles/colors';
import ImagePreviewModal from '../../../../../../../../elements/ImagePreviewModal';
import FileMessage from './FileMessage';

import type {
  ImageSource,
  ConversationMessage,
} from '../../../../../../../../entities';
import ImageRenderer from '../../../../../../../../elements/ImageRenderer';
import VideoMessage from './VideoMessage';
import AudioMessage from './AudioMessage';
import MessageFormatter from '../../../../../../../../elements/TextMessage/message-formatter';

type Props = {
  message: ConversationMessage,
};

const useStyles = props =>
  makeStyles((theme: Theme) => ({
    message: {
      width: '100%',
      paddingLeft: props.self ? '.75rem' : 0,
      paddingRight: !props.self ? '.75rem' : 0,
    },
    content: {
      width: '100%',
      display: 'flex',
      alignItems: 'flex-end',
      gap: '.5rem',

      '&[data-self="true"]': {
        flexDirection: 'row-reverse',
      },
    },
    messageWrap: {
      display: 'flex',
      width: '90%',
      flexDirection: 'column',
      gap: '.5rem',
      '&[data-self="true"]': {
        alignItems: 'flex-end',
      },
    },
    textMessageWrap: {
      width: 'auto',
      maxWidth: '100%',
      padding: '.75rem',
      borderRadius: `8px 8px ${props.self ? '0px 8px' : '8px 0px'}`,
      background: props.self ? '#c5c5c7' : '#f4f4f6',
      alignSelf: 'flex-start',
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      fontSize: 14,

      '&[data-self="true"]': {
        alignSelf: 'flex-end',
      },
    },
    text: {
      color: props.self ? colors.grey900 : colors.grey800,
    },
    pictureMessageWrap: {
      gap: '.5rem',
      background: props.self ? '#d5bfff' : '#F4F4F6',
      borderRadius: '.75rem',
      padding: '.5rem',
      width: '90%',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      maxWidth: '320px',
      position: 'relative',

      '&[data-single="true"]': {
        gridTemplateColumns: '1fr',
        aspectRatio: '1/1',
      },

      '&:hover .see-more-picture-button': {
        opacity: 1,
      },
      '&:focus-within .see-more-picture-button': {
        opacity: 1,
      },
    },
    pictureMessage: {
      width: '100%',
      height: '80px',
      borderRadius: '.75rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      overflow: 'hidden',

      '&[data-single="true"]': {
        height: 'auto !important',
      },

      [theme.breakpoints.up('xl')]: {
        height: '120px',
      },

      '&:nth-child(1)': {
        gridColumn: '1 / 3',
      },

      '&:nth-child(2)': {
        gridColumn: '3 / 4',
      },

      '&:nth-child(3)': {
        gridColumn: '1 / 2',
      },

      '&:nth-child(4)': {
        gridColumn: '2 / 4',
      },

      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform .2s ease-in-out',
        borderRadius: 'inherit',
      },

      '&:hover img': {
        transform: 'scale(1.1)',
      },
    },
    seeMorePictureButton: {
      position: 'absolute',
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      background: `${colors.grey200}80`,
      backdropFilter: 'blur(1rem)',
      color: colors.white,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      border: 'none',
      opacity: 0,
      fontWeight: 500,
      transition: 'opacity .2s ease-in-out',

      top: '50%',
      transform: 'translateY(-50%)',
      right: '-2.25rem',

      '&[data-self="true"]': {
        left: '-2.25rem',
        right: 'auto',
      },
    },
    fileMessageWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.5rem',
      maxWidth: '100%',
    },
  }));

export default function MessageArchived({message}: Props) {
  const {
    text,
    fromMe: self,
    images,
    documents,
    videos,
    audios,
    isUnreachable,
  } = message;

  // const {self, text, pictures, files} = props;
  const classes = useStyles({self})();

  const [imagePreview, setImagePreview] = useState<null | ImageSource>(null);

  return (
    <div className={classes.message}>
      {imagePreview && images && (
        <ImagePreviewModal
          open={!!imagePreview}
          onClose={() => setImagePreview(null)}
          images={images.filter(img => img.src).map(img => img.src || '')}
          defaultImage={imagePreview}
        />
      )}
      <div className={classes.content} data-self={self}>
        <CustomAvatar />
        <div className={classes.messageWrap} data-self={self}>
          {text && (
            <div className={classes.textMessageWrap} data-self={self}>
              <MessageFormatter
                message={text}
                chatServiceType={message.service}
              />
            </div>
          )}

          {images && (
            <div
              className={classes.pictureMessageWrap}
              data-single={images.length === 1}
            >
              {images
                .filter(img => img.src)
                .slice(0, 4)
                .map((picture, index) => (
                  <button
                    key={index}
                    className={classes.pictureMessage}
                    onClick={() => setImagePreview(picture.src || '')}
                    data-single={images.length === 1}
                  >
                    {picture.src && <ImageRenderer src={picture.src} alt="" />}
                  </button>
                ))}

              {images.length > 4 && (
                <button
                  className={`${classes.seeMorePictureButton} see-more-picture-button`}
                  data-self={self}
                  onClick={() => {
                    setImagePreview(images[4].src || '');
                  }}
                >
                  {images.length - 4}+
                </button>
              )}
            </div>
          )}

          {documents && (
            <div className={classes.fileMessageWrap}>
              {documents
                .filter(file => file.src)
                .map((file, index) => {
                  if (!file.src) return null;
                  return (
                    <FileMessage
                      key={index}
                      src={file.src}
                      name={file.name}
                      self={self}
                      size={file.size}
                      isUnreachable={isUnreachable}
                      type={file.mimetype}
                    />
                  );
                })}
            </div>
          )}

          {videos &&
            videos.map((video, index) => (
              <VideoMessage key={index} self={self} video={video} />
            ))}

          {audios &&
            audios.map((audio, index) => (
              <AudioMessage key={index} self={self} audio={audio} />
            ))}
        </div>
      </div>
    </div>
  );
}
